import { lerpColor } from '../../color';
import { hsl, type World as Look } from '../../world';
import { calmAt, calmCenter, glowDot, grain, rgba, sketchCircle, spots, starPath, vignette, type Painter, type Spot } from '../../art/paint';

const TAU = Math.PI * 2;
const SQUASH = 0.9;

interface Frond {
  readonly a: number;
  readonly len: number;
  readonly droop: number;
}

interface Stem {
  readonly x: number;
  readonly h: number;
  readonly lean: number;
  readonly fronds: readonly Frond[];
}

interface Bump {
  readonly x: number;
  readonly rx: number;
  readonly ry: number;
}

interface Clump {
  readonly x: number;
  readonly y: number;
  readonly rx: number;
  readonly ry: number;
  readonly phase: number;
}

interface Fin {
  readonly side: number;
  readonly h: number;
  readonly w: number;
}

interface Giant {
  readonly x: number;
  readonly s: number;
  readonly top: number;
  readonly bend: number;
  readonly fins: readonly Fin[];
  readonly crown: readonly Clump[];
}

interface Scene {
  readonly portrait: boolean;
  readonly s: number;
  readonly horizon: number;
  readonly moon: Spot;
  readonly moonR: number;
  readonly canoe: { readonly x: number; readonly y: number; readonly len: number };
  readonly leaf: number;
  readonly stems: readonly Stem[];
  readonly bush: readonly Bump[];
  readonly giant: Giant;
}

interface Tones {
  readonly bg: number;
  readonly ink: number;
  readonly rim: number;
  readonly far: number;
  readonly palm: number;
  readonly trunk: number;
  readonly crownTop: number;
  readonly crownLow: number;
  readonly leaf: number;
  readonly vein: number;
  readonly hull: number;
}

function open(p: Painter, x: number, y: number): number {
  const dx = (x - p.cx) / (p.unit * 1.08);
  const dy = (y - p.cy + 0.08 * p.unit) / p.unit;
  return Math.min(1, Math.max(0, (Math.hypot(dx, dy) - 0.55) / 0.45));
}

function growPalms(p: Painter, s: number): Stem[] {
  const out: Stem[] = [];
  let x = -0.03 * p.w;
  while (x < p.w * 1.03) {
    const n = p.rng.int(2, 5);
    for (let i = 0; i < n; i++) {
      const count = p.rng.int(6, 9);
      const fronds: Frond[] = [];
      for (let f = 0; f < count; f++) {
        const u = f / (count - 1);
        fronds.push({
          a: -Math.PI * (0.06 + 0.88 * u) + p.rng.range(-0.12, 0.12),
          len: p.rng.range(0.045, 0.07) * s,
          droop: p.rng.range(0.25, 0.6),
        });
      }
      out.push({ x: x + p.rng.range(-0.025, 0.025) * s, h: p.rng.range(0.1, 0.24) * s, lean: p.rng.range(-0.14, 0.14), fronds });
    }
    x += p.rng.range(0.1, 0.2) * s;
  }
  return out;
}

function growBush(p: Painter, s: number): Bump[] {
  const out: Bump[] = [];
  let x = -0.02 * p.w;
  while (x < p.w * 1.02) {
    out.push({ x, rx: p.rng.range(0.03, 0.06) * s, ry: p.rng.range(0.012, 0.045) * s });
    x += p.rng.range(0.025, 0.06) * s;
  }
  return out;
}

function growGiant(p: Painter, x: number, s: number, horizon: number): Giant {
  const fins: Fin[] = [];
  for (let i = 0; i < 4; i++) {
    fins.push({ side: i % 2 === 0 ? -1 : 1, h: p.rng.range(0.07, 0.13) * s, w: p.rng.range(0.06, 0.12) * s * (i < 2 ? 1 : 0.7) });
  }
  const top = horizon - 0.3 * s;
  const crown: Clump[] = [];
  const n = 7;
  for (let i = 0; i < n; i++) {
    const u = (i / (n - 1)) * 2 - 1;
    crown.push({
      x: x + u * 0.27 * s + p.rng.range(-0.02, 0.02) * s,
      y: top - 0.045 * s - 0.035 * s * (1 - u * u) + p.rng.range(-0.01, 0.01) * s,
      rx: p.rng.range(0.085, 0.12) * s,
      ry: p.rng.range(0.032, 0.046) * s,
      phase: p.rng.float() * TAU,
    });
  }
  return { x, s, top, bend: p.rng.range(-0.015, 0.015) * s, fins, crown };
}

function layout(p: Painter): Scene {
  const portrait = p.w / p.h < 0.8;
  const s = portrait ? Math.min(p.unit, 0.2 * p.h) : p.unit;
  const horizon = (portrait ? 0.31 : 0.42) * p.h;
  const moon = portrait ? { x: 0.84 * p.w, y: 0.215 * p.h } : { x: 0.925 * p.w, y: 0.24 * p.h };
  const moonR = portrait ? Math.max(12, 0.075 * s) : Math.max(14, 0.06 * s);
  const len = portrait ? Math.min(0.42 * p.w, 0.75 * s) : 0.6 * s;
  const canoe = portrait
    ? { x: 0.06 * p.w + len / 2, y: Math.max(p.cy + 0.95 * p.unit, 0.78 * p.h), len }
    : { x: 0.025 * p.w + len / 2, y: 0.66 * p.h, len };
  const giant = growGiant(p, portrait ? 0.24 * p.w : 0.8 * p.w, portrait ? s : 0.8 * s, horizon);
  return { portrait, s, horizon, moon, moonR, canoe, leaf: portrait ? 1.3 : 1, stems: growPalms(p, s), bush: growBush(p, s), giant };
}

function sky(p: Painter, sc: Scene, t: Tones): void {
  const ctx = p.ctx;
  const g = ctx.createLinearGradient(0, 0, 0, sc.horizon);
  g.addColorStop(0, rgba(lerpColor(t.bg, t.ink, 0.5)));
  g.addColorStop(0.65, rgba(t.bg));
  g.addColorStop(1, rgba(lerpColor(t.bg, t.far, 0.35)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, sc.horizon + 1);
}

function stars(p: Painter, sc: Scene, t: Tones): void {
  const ctx = p.ctx;
  let placed = 0;
  for (let i = 0; i < 80 && placed < 12; i++) {
    const x = p.rng.range(0.03, 0.97) * p.w;
    const y = p.rng.range(0.03 * p.h, sc.horizon - 0.27 * sc.s);
    if (Math.hypot(x - sc.moon.x, y - sc.moon.y) < sc.moonR * 5 || calmAt(p, x, y, 0.9)) continue;
    const a = p.rng.range(0.25, 0.5);
    if (p.rng.chance(0.3)) {
      starPath(ctx, x, y, p.rng.range(2.4, 3.6), 0.32, 4, p.rng.range(-0.2, 0.2));
      ctx.fillStyle = rgba(t.rim, a);
      ctx.fill();
    } else {
      ctx.beginPath();
      ctx.arc(x, y, p.rng.range(0.7, 1.3), 0, TAU);
      ctx.fillStyle = rgba(t.rim, a);
      ctx.fill();
    }
    placed++;
  }
}

function moon(p: Painter, sc: Scene, t: Tones, ceil: number): void {
  const ctx = p.ctx;
  const { x, y } = sc.moon;
  const r = sc.moonR;
  glowDot(ctx, x, y, r * 5, t.rim, 0.1);
  glowDot(ctx, x, y, r * 2, t.rim, 0.14);
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fillStyle = rgba(lerpColor(ceil, t.rim, 0.62));
  ctx.fill();
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.clip();
  ctx.fillStyle = rgba(lerpColor(ceil, t.rim, 0.4), 0.5);
  for (let i = 0; i < 3; i++) {
    ctx.beginPath();
    ctx.ellipse(x + p.rng.range(-0.4, 0.4) * r, y + p.rng.range(-0.4, 0.4) * r, r * p.rng.range(0.14, 0.26), r * p.rng.range(0.1, 0.2), p.rng.float() * Math.PI, 0, TAU);
    ctx.fill();
  }
  ctx.beginPath();
  ctx.arc(x - 1.45 * r, y, r, 0, TAU);
  ctx.fillStyle = rgba(lerpColor(t.bg, t.ink, 0.2), 0.55);
  ctx.fill();
  ctx.restore();
  sketchCircle(ctx, x, y, r, p.rng, 0.03);
  ctx.strokeStyle = rgba(t.rim, 0.35);
  ctx.lineWidth = 1.2;
  ctx.stroke();
}

function river(p: Painter, sc: Scene, t: Tones): void {
  const ctx = p.ctx;
  const g = ctx.createLinearGradient(0, sc.horizon, 0, p.h);
  g.addColorStop(0, rgba(lerpColor(t.bg, t.far, 0.5)));
  g.addColorStop(0.35, rgba(t.bg));
  g.addColorStop(1, rgba(lerpColor(t.bg, t.ink, 0.4)));
  ctx.fillStyle = g;
  ctx.fillRect(0, sc.horizon, p.w, p.h - sc.horizon);
}

function bush(ctx: CanvasRenderingContext2D, sc: Scene, color: number): void {
  ctx.fillStyle = rgba(color);
  ctx.beginPath();
  for (const b of sc.bush) {
    ctx.moveTo(b.x - b.rx, sc.horizon + 1);
    ctx.ellipse(b.x, sc.horizon + 1, b.rx, b.ry, 0, Math.PI, TAU);
    ctx.closePath();
  }
  ctx.fill();
  ctx.fillRect(-2, sc.horizon - 1, ctx.canvas.width + 4, 3);
}

function quad(p0: number, c: number, p1: number, u: number): number {
  return (1 - u) * (1 - u) * p0 + 2 * (1 - u) * u * c + u * u * p1;
}

function palms(ctx: CanvasRenderingContext2D, sc: Scene, color: number): void {
  const H = sc.horizon;
  const s = sc.s;
  ctx.strokeStyle = rgba(color);
  ctx.lineCap = 'round';
  ctx.lineWidth = Math.max(1.2, 0.01 * s);
  ctx.beginPath();
  for (const st of sc.stems) {
    ctx.moveTo(st.x, H + 1);
    ctx.quadraticCurveTo(st.x + st.lean * st.h * 0.25, H - st.h * 0.5, st.x + st.lean * st.h, H - st.h);
  }
  ctx.stroke();
  const ribs = new Path2D();
  const leaflets = new Path2D();
  for (const st of sc.stems) {
    const tx = st.x + st.lean * st.h;
    const ty = H - st.h;
    for (const f of st.fronds) {
      const ca = Math.cos(f.a);
      const sa = Math.sin(f.a);
      const cx = tx + ca * f.len * 0.55;
      const cy = ty + sa * f.len * 0.55 - 0.2 * f.len;
      const ex = tx + ca * f.len;
      const ey = ty + sa * f.len + f.droop * f.len;
      ribs.moveTo(tx, ty);
      ribs.quadraticCurveTo(cx, cy, ex, ey);
      for (let u = 0.3; u < 0.95; u += 0.12) {
        const bx = quad(tx, cx, ex, u);
        const by = quad(ty, cy, ey, u);
        leaflets.moveTo(bx, by);
        leaflets.lineTo(bx + ca * 0.1 * f.len, by + 0.38 * f.len * (1 - 0.4 * u));
      }
    }
  }
  ctx.lineWidth = Math.max(1.1, 0.0075 * s);
  ctx.stroke(ribs);
  ctx.lineWidth = Math.max(0.8, 0.004 * s);
  ctx.stroke(leaflets);
}

function clumpPath(path: Path2D, c: Clump, from = 0, to = TAU): void {
  const steps = 36;
  for (let i = 0; i <= steps; i++) {
    const a = from + ((to - from) * i) / steps;
    const up = Math.sin(a) < 0;
    const m = up ? 1 + 0.16 * Math.abs(Math.sin(5 * a + c.phase)) : 1;
    const x = c.x + Math.cos(a) * c.rx * m;
    const y = c.y + Math.sin(a) * c.ry * m * (up ? 1 : 0.55);
    if (i === 0) path.moveTo(x, y);
    else path.lineTo(x, y);
  }
  if (to - from >= TAU) path.closePath();
}

function giant(ctx: CanvasRenderingContext2D, sc: Scene, t: Tones): void {
  const gt = sc.giant;
  const H = sc.horizon;
  const { x, s, top } = gt;
  ctx.fillStyle = rgba(t.trunk);
  ctx.beginPath();
  ctx.moveTo(x - 0.045 * s, H + 1);
  ctx.quadraticCurveTo(x - 0.026 * s + gt.bend * 0.5, H - 0.15 * s, x - 0.02 * s + gt.bend, top + 0.01 * s);
  ctx.lineTo(x + 0.02 * s + gt.bend, top + 0.01 * s);
  ctx.quadraticCurveTo(x + 0.026 * s + gt.bend * 0.5, H - 0.15 * s, x + 0.045 * s, H + 1);
  ctx.closePath();
  ctx.fill();
  for (const f of gt.fins) {
    ctx.beginPath();
    ctx.moveTo(x + f.side * 0.03 * s, H - f.h);
    ctx.quadraticCurveTo(x + f.side * f.w * 0.3, H - f.h * 0.15, x + f.side * f.w, H + 1);
    ctx.lineTo(x, H + 1);
    ctx.closePath();
    ctx.fill();
  }
  ctx.strokeStyle = rgba(t.trunk);
  ctx.lineCap = 'round';
  const sx = x + gt.bend;
  const sy = top + 0.02 * s;
  for (const c of gt.crown) {
    ctx.lineWidth = Math.max(1.2, 0.012 * s);
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.quadraticCurveTo(sx + (c.x - sx) * 0.5, sy - 0.01 * s, c.x, c.y + c.ry * 0.3);
    ctx.stroke();
  }
  let yTop = top;
  for (const c of gt.crown) yTop = Math.min(yTop, c.y - c.ry * 1.2);
  const grad = ctx.createLinearGradient(0, yTop, 0, top + 0.01 * s);
  grad.addColorStop(0, rgba(t.crownTop));
  grad.addColorStop(1, rgba(t.crownLow));
  const crown = new Path2D();
  for (const c of gt.crown) clumpPath(crown, c);
  ctx.fillStyle = grad;
  ctx.fill(crown);
  const lit = new Path2D();
  for (const c of gt.crown) clumpPath(lit, c, -Math.PI * 0.62, -Math.PI * 0.04);
  ctx.strokeStyle = rgba(t.rim, 0.22);
  ctx.lineWidth = Math.max(1, 0.006 * s);
  ctx.stroke(lit);
}

function land(ctx: CanvasRenderingContext2D, sc: Scene, t: Tones): void {
  bush(ctx, sc, t.far);
  palms(ctx, sc, t.palm);
  giant(ctx, sc, t);
}

function reflection(p: Painter, sc: Scene, t: Tones): void {
  const k = p.ctx.getTransform().a || 1;
  const depth = p.h - sc.horizon;
  const off = document.createElement('canvas');
  off.width = Math.max(1, Math.ceil(p.w * k));
  off.height = Math.max(1, Math.ceil(depth * k));
  const o = off.getContext('2d');
  if (o === null) return;
  o.scale(k, k);
  o.translate(0, sc.horizon * SQUASH);
  o.scale(1, -SQUASH);
  land(o, sc, t);
  let y = 0;
  while (y < depth) {
    const band = Math.min(depth - y, p.rng.range(2, 7));
    const u = y / depth;
    const dx = p.rng.range(-1, 1) * sc.s * (0.004 + 0.02 * u);
    p.ctx.globalAlpha = 0.32 * (1 - 0.7 * u);
    p.ctx.drawImage(off, 0, y * k, p.w * k, band * k, dx, sc.horizon + y, p.w, band);
    y += band;
  }
  p.ctx.globalAlpha = 1;
}

function glitter(p: Painter, sc: Scene, t: Tones): void {
  const ctx = p.ctx;
  const mid = sc.horizon + (sc.horizon - sc.moon.y) * SQUASH;
  const top = sc.horizon + 0.012 * sc.s;
  const bottom = Math.min(p.h * 0.9, mid + (mid - sc.horizon) * 2.2);
  ctx.lineCap = 'round';
  let y = top;
  while (y < bottom) {
    const u = Math.abs(y - mid) / Math.max(1, bottom - top);
    y += p.rng.range(5, 13) * (0.6 + u);
    if (p.rng.chance(0.25)) continue;
    const half = sc.moonR * (1.1 - u * 0.6) * p.rng.range(0.25, 1.2);
    const x = sc.moon.x + p.rng.range(-0.6, 0.6) * sc.moonR;
    const a = 0.24 * (1 - u) * p.rng.range(0.4, 1) * (0.25 + 0.75 * open(p, x, y));
    ctx.beginPath();
    ctx.moveTo(x - half, y);
    ctx.quadraticCurveTo(x, y + p.rng.range(-1.2, 1.2), x + half, y + p.rng.range(-0.8, 0.8));
    ctx.strokeStyle = rgba(t.rim, Math.min(0.24, a));
    ctx.lineWidth = p.rng.range(0.9, 2);
    ctx.stroke();
  }
}

function currents(p: Painter, sc: Scene, color: number): void {
  const ctx = p.ctx;
  const n = sc.portrait ? 10 : 15;
  ctx.lineCap = 'round';
  for (let i = 0; i < n; i++) {
    const len = p.rng.range(0.04, 0.2) * sc.s;
    const x = Math.min(p.w - len / 2 - 2, Math.max(len / 2 + 2, p.rng.range(0.02, 0.98) * p.w));
    const y = p.rng.range(sc.horizon + 0.02 * sc.s, p.h * 0.92);
    const a = p.rng.range(0.08, 0.18) * (0.3 + 0.7 * open(p, x, y));
    ctx.beginPath();
    ctx.moveTo(x - len / 2, y);
    ctx.quadraticCurveTo(x - len / 6, y + p.rng.range(-1.5, 1.5), x, y + p.rng.range(-0.8, 0.8));
    ctx.quadraticCurveTo(x + len / 6, y + p.rng.range(-1.5, 1.5), x + len / 2, y);
    ctx.strokeStyle = rgba(color, a);
    ctx.lineWidth = p.rng.range(0.8, 1.6);
    ctx.stroke();
  }
}

function lily(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, look: Look): void {
  const pal = look.palette;
  for (let i = 0; i < 7; i++) {
    const a = -Math.PI / 2 + (i - 3) * 0.36;
    ctx.beginPath();
    ctx.ellipse(x + Math.cos(a) * r * 0.45, y + Math.sin(a) * r * 0.45, r * 0.5, r * 0.2, a, 0, TAU);
    ctx.fillStyle = rgba(lerpColor(pal.held, pal.heldLight, i % 2 === 0 ? 0 : 0.4), 0.55);
    ctx.fill();
  }
  ctx.beginPath();
  ctx.arc(x, y - r * 0.12, r * 0.18, 0, TAU);
  ctx.fillStyle = rgba(pal.spot, 0.6);
  ctx.fill();
}

function pads(p: Painter, sc: Scene, t: Tones, look: Look, ceil: number): void {
  const ctx = p.ctx;
  const n = sc.portrait ? 4 : 6;
  const spacing = 0.12 * sc.s;
  const out: Spot[] = [];
  for (let i = 0; i < 120 && out.length < n; i++) {
    const x = p.rng.range(0.03, 0.97) * p.w;
    const y = p.rng.range(sc.horizon + 0.06 * sc.s, p.h * 0.9);
    if (calmAt(p, x, y, 1)) continue;
    if (Math.hypot(x - sc.canoe.x, y - sc.canoe.y) < sc.canoe.len * 0.6) continue;
    if (out.some((o) => Math.hypot(o.x - x, o.y - y) < spacing)) continue;
    out.push({ x, y });
  }
  out.forEach((o, i) => {
    const depth = (o.y - sc.horizon) / (p.h - sc.horizon);
    const r = p.rng.range(0.05, 0.085) * sc.s * (sc.portrait ? 1.1 : 1) * (0.7 + 0.5 * depth);
    const x = Math.min(p.w - r - 2, Math.max(r + 2, o.x));
    const ry = r * 0.34;
    ctx.beginPath();
    ctx.ellipse(x, o.y + ry * 0.35, r * 1.02, ry, 0, 0, TAU);
    ctx.fillStyle = rgba(t.ink, 0.3);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(x, o.y, r, ry, 0, 0, TAU);
    ctx.fillStyle = rgba(lerpColor(t.far, ceil, 0.55), 0.92);
    ctx.fill();
    ctx.beginPath();
    for (let v = 0; v < 8; v++) {
      const a = (v / 8) * TAU + 0.2;
      ctx.moveTo(x, o.y);
      ctx.lineTo(x + Math.cos(a) * r * 0.8, o.y + Math.sin(a) * ry * 0.8);
    }
    ctx.strokeStyle = rgba(lerpColor(ceil, t.rim, 0.15), 0.25);
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(x, o.y, r, ry, 0, 0, TAU);
    ctx.strokeStyle = rgba(lerpColor(ceil, t.rim, 0.35), 0.55);
    ctx.lineWidth = Math.max(1, r * 0.12);
    ctx.stroke();
    if (p.level >= 4 && i % 2 === 0) lily(ctx, x + r * 0.3, o.y - ry * 0.4, r * 0.55, look);
  });
}

function canoe(p: Painter, sc: Scene, t: Tones, look: Look): void {
  const ctx = p.ctx;
  const { x, y, len } = sc.canoe;
  const half = len / 2;
  const depth = len * 0.11;
  const lift = len * 0.06;
  ctx.beginPath();
  ctx.ellipse(x, y + depth * 0.95, half * 0.95, depth * 0.45, 0, 0, TAU);
  ctx.fillStyle = rgba(t.ink, 0.35);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(x - half, y - lift);
  ctx.quadraticCurveTo(x, y + depth * 0.25, x + half, y - lift * 1.3);
  ctx.quadraticCurveTo(x + half * 0.7, y + depth, x, y + depth);
  ctx.quadraticCurveTo(x - half * 0.75, y + depth, x - half, y - lift);
  ctx.closePath();
  ctx.fillStyle = rgba(t.hull);
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink, 0.9);
  ctx.lineWidth = Math.max(1.2, 0.006 * sc.s);
  ctx.lineJoin = 'round';
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x - half * 0.92, y - lift * 0.8);
  ctx.quadraticCurveTo(x, y - depth * 0.1, x + half * 0.92, y - lift * 1.1);
  ctx.quadraticCurveTo(x, y + depth * 0.3, x - half * 0.92, y - lift * 0.8);
  ctx.closePath();
  ctx.fillStyle = rgba(lerpColor(t.far, look.ceil, 0.6), 0.9);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(x - half, y - lift);
  ctx.quadraticCurveTo(x, y + depth * 0.25, x + half, y - lift * 1.3);
  ctx.strokeStyle = rgba(t.rim, 0.5);
  ctx.lineWidth = 1.6;
  ctx.stroke();
  const lx = x - half * 0.78;
  const ly = y - lift - depth * 1.6;
  ctx.beginPath();
  ctx.moveTo(lx, y - lift * 0.9);
  ctx.lineTo(lx, ly);
  ctx.strokeStyle = rgba(t.ink, 0.9);
  ctx.lineWidth = Math.max(1, 0.004 * sc.s);
  ctx.stroke();
  glowDot(ctx, lx, ly, len * 0.14, look.palette.spot, 0.42);
  ctx.beginPath();
  ctx.arc(lx, ly, Math.max(1.5, len * 0.014), 0, TAU);
  ctx.fillStyle = rgba(look.palette.spot, 0.6);
  ctx.fill();
  for (let i = 0; i < 4; i++) {
    const yy = y + depth * (1.6 + i * 0.7);
    const w = len * (0.06 - i * 0.01);
    ctx.beginPath();
    ctx.moveTo(lx - w, yy);
    ctx.lineTo(lx + w, yy);
    ctx.strokeStyle = rgba(look.palette.spot, 0.2 - i * 0.04);
    ctx.lineWidth = 1.2;
    ctx.stroke();
  }
}

function sagittate(ctx: CanvasRenderingContext2D, x: number, y: number, L: number, rot: number, veins: Path2D): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  ctx.scale(L, L);
  ctx.beginPath();
  ctx.moveTo(0, -1);
  ctx.quadraticCurveTo(0.58, -0.6, 0.5, 0.02);
  ctx.quadraticCurveTo(0.52, 0.34, 0.38, 0.44);
  ctx.quadraticCurveTo(0.16, 0.2, 0, 0.1);
  ctx.quadraticCurveTo(-0.16, 0.2, -0.38, 0.44);
  ctx.quadraticCurveTo(-0.52, 0.34, -0.5, 0.02);
  ctx.quadraticCurveTo(-0.58, -0.6, 0, -1);
  ctx.closePath();
  const m = new DOMMatrix().translateSelf(x, y).rotateSelf((rot * 180) / Math.PI).scaleSelf(L, L);
  const v = new Path2D();
  v.moveTo(0, 0.1);
  v.lineTo(0, -0.88);
  for (let i = 0; i < 3; i++) {
    const yy = -0.15 - i * 0.22;
    v.moveTo(0, yy);
    v.lineTo(0.36 - i * 0.06, yy - 0.18);
    v.moveTo(0, yy);
    v.lineTo(-0.36 + i * 0.06, yy - 0.18);
  }
  v.moveTo(0, 0.1);
  v.lineTo(0.34, 0.38);
  v.moveTo(0, 0.1);
  v.lineTo(-0.34, 0.38);
  veins.addPath(v, m);
  ctx.restore();
}

function corner(p: Painter, sc: Scene, t: Tones, side: number, count: number, tall: number): void {
  const ctx = p.ctx;
  const f = sc.leaf;
  const s = sc.s;
  const reach = sc.portrait ? 0.22 : 0.14;
  const roots = new Path2D();
  for (let i = 0; i < 5; i++) {
    const span = p.rng.range(0.03, 0.08) * s * f;
    const peak = p.rng.range(0.03, 0.08) * s * f;
    const x0 = side < 0 ? p.rng.range(0, reach * 0.8) * p.w : p.w - p.rng.range(0, reach * 0.8) * p.w;
    const x1 = Math.min(p.w, Math.max(0, x0 + side * span));
    roots.moveTo(x0, p.h + 2);
    roots.quadraticCurveTo((x0 + x1) / 2, p.h + 2 - peak * 2, x1, p.h + 2);
  }
  ctx.lineCap = 'round';
  ctx.strokeStyle = rgba(t.ink, 0.85);
  ctx.lineWidth = Math.max(2.4, 0.014 * s * f);
  ctx.stroke(roots);
  ctx.strokeStyle = rgba(lerpColor(t.bg, t.vein, 0.3));
  ctx.lineWidth = Math.max(1.2, 0.007 * s * f);
  ctx.stroke(roots);
  const leaves: { x: number; y: number; L: number; rot: number; bx: number; bend: number }[] = [];
  for (let i = 0; i < count; i++) {
    const L = p.rng.range(0.08, 0.13) * s * f;
    const lift = p.rng.range(0.14, 0.38) * s * f * tall;
    const off = p.rng.range(0.02, reach) * p.w;
    const bx = side < 0 ? off : p.w - off;
    const lean = p.rng.range(0.02, 0.06) * s * f;
    const x = Math.min(p.w - 0.62 * L, Math.max(0.62 * L, bx + side * lean));
    leaves.push({ x, y: p.h - lift, L, rot: side * p.rng.range(0.12, 0.6), bx, bend: side * lean * 0.3 });
  }
  leaves.sort((a, b) => a.y - b.y);
  for (const l of leaves) {
    ctx.beginPath();
    ctx.moveTo(l.bx, p.h + 2);
    ctx.quadraticCurveTo(l.bx + l.bend, l.y + (p.h - l.y) * 0.5, l.x, l.y);
    ctx.strokeStyle = rgba(t.ink, 0.85);
    ctx.lineWidth = Math.max(2.2, 0.012 * s * f);
    ctx.stroke();
    ctx.strokeStyle = rgba(lerpColor(t.leaf, t.vein, 0.25));
    ctx.lineWidth = Math.max(1, 0.006 * s * f);
    ctx.stroke();
    const veins = new Path2D();
    sagittate(ctx, l.x, l.y, l.L, l.rot, veins);
    ctx.fillStyle = rgba(t.leaf);
    ctx.fill();
    ctx.strokeStyle = rgba(t.ink, 0.85);
    ctx.lineWidth = Math.max(1.4, 0.007 * s * f);
    ctx.lineJoin = 'round';
    ctx.stroke();
    ctx.strokeStyle = rgba(t.vein, 0.3);
    ctx.lineWidth = 1;
    ctx.stroke(veins);
  }
}

function fireflies(p: Painter, sc: Scene, look: Look): void {
  const ctx = p.ctx;
  const n = (sc.portrait ? 10 : 15) + (p.level - 1) * 2;
  const list = spots(p, n * 2, 0.95, 0.05 * Math.min(p.w, p.h), 0.07 * p.unit)
    .filter((s) => s.y > 0.21 * p.h && s.y < 0.86 * p.h)
    .slice(0, n);
  for (const f of list) {
    const r = p.rng.range(1.2, 2.2);
    glowDot(ctx, f.x, f.y, r * 5, look.palette.spot, 0.2);
    ctx.beginPath();
    ctx.arc(f.x, f.y, r, 0, TAU);
    ctx.fillStyle = rgba(look.palette.spot, p.rng.range(0.35, 0.6));
    ctx.fill();
  }
}

export function paint(p: Painter, look: Look): void {
  const pal = look.palette;
  const far = hsl(look.hue, 38, 16);
  const t: Tones = {
    bg: pal.bg,
    ink: pal.ink,
    rim: pal.rim,
    far,
    palm: lerpColor(far, look.ceil, 0.18),
    trunk: lerpColor(far, look.ceil, 0.3),
    crownTop: lerpColor(far, look.ceil, 0.6),
    crownLow: lerpColor(far, pal.ink, 0.25),
    leaf: lerpColor(pal.bg, look.ceil, 0.55),
    vein: lerpColor(look.ceil, pal.rim, 0.2),
    hull: lerpColor(pal.bg, pal.ink, 0.55),
  };
  const sc = layout(p);
  sky(p, sc, t);
  if (p.level >= 3) stars(p, sc, t);
  moon(p, sc, t, look.ceil);
  river(p, sc, t);
  reflection(p, sc, t);
  glitter(p, sc, t);
  currents(p, sc, pal.path);
  land(p.ctx, sc, t);
  if (p.level >= 2) pads(p, sc, t, look, look.ceil);
  canoe(p, sc, t, look);
  corner(p, sc, t, -1, 3, 0.8);
  corner(p, sc, t, 1, sc.portrait ? 4 : 5, sc.portrait ? 1.1 : 1.3);
  fireflies(p, sc, look);
  calmCenter(p, pal.bg, 0.6, 1.1);
  grain(p, 0.05, true);
  vignette(p, pal.ink, 0.45);
}
